import Dashboard from '@/pages/Dashboard'
import Sidebar from '@/components/sidebar/Sidebar'
import { Box, useTheme } from '@mui/material'
import { useAuth } from '@/auth/AuthContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_OPEN_WIDTH } from '@/components/sidebar/sidebarMotion'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation, 
} from 'react-router-dom'
import Map from '@/pages/Map'
import Consumers from './pages/Consumers'
import ConsumersLayout from './pages/consumer/ConsumerLayout'
import ConsumerDetailsLayout from './pages/consumer/ConsumerDetailsLayout'
import ConsumerDetails from './pages/consumer/ConsumerDetails'
import ConsumerCalls from './pages/consumer/calls/ConsumerCalls'
import ConsumerNotifications from './pages/consumer/ConsumerNotifications'
import ConsumerHistory from './pages/consumer/ConsumerHistory'
import ConsumerCRM from './pages/consumer/CRM/ConsumerCRM'
import General from './pages/consumer/General'
import CRM from './pages/CRM'
import Notifications from './pages/notification/Notifications'
import Details from './pages/notification/Details'
import Analytics from './pages/Analytics'
import AnalyticsLayout from './pages/analytics/AnalyticsLayout'
import AnalyticsDetailsLayout from './pages/analytics/AnalyticsDetailsLayout'
import AnalyticsDetails from './pages/analytics/AnalyticsDetails'
import AnalyticsConsumerTable from './pages/analytics/AnalyticsConsumerTable'
import WaterTreeMap from './pages/analytics/WaterTreeMap'
import NotificationFlow from './pages/NotificationFlow'
import NotificationManage from './pages/notification/NotificationManage'
import SensorsLayout from './pages/sensors/SensorsLayout'
import Sensors from './pages/sensors/Sensors'
import SensorsGraph from './pages/sensors/SensorsGraph'
import SensorsDetailsLayout from './pages/sensors/SensorsDetailsLayout'
import SensorDetails from './pages/sensors/SensorDetails'
import SensorsHistory from './pages/sensors/SensorsHistory'
import SensorsGeneral from './pages/sensors/SensorsGeneral'

import NotificationsGeneral from './pages/notification/NotificationGeneral'
import ReportGeneration from './pages/ReportGeneration'
import History from './pages/History'
import Setting from './pages/Setting'
import Organization from './pages/Settings/Organization'
import UsersSettings from './pages/Settings/Users'

import ProtectedRoute from '@/auth/ProtectedRoute'
import LoginPage from '@/pages/auth/LoginPage'
import OtpPage from './pages/auth/Otp'
import ChangePasswordPage from './pages/auth/ChangePassword'
import SiteSelection from './pages/SiteSelection'
import SiteProtectedRoute from '@/auth/SiteProtectedRoute'

const AppContent = () => {
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_COLLAPSED_WIDTH)
  const theme = useTheme()
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()
  const pageBackground =
    theme.palette.mode === 'dark'
      ? '#192336'
      : '#F8FAFD'

  const handleSidebarChange = useCallback((isCollapsed: boolean) => {
    setSidebarWidth(isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_OPEN_WIDTH)
  }, [])

  // MUI Tabs and similar components measure on resize; tell them once the page has its new width.
  const firstWidth = useRef(true)
  useEffect(() => {
    if (firstWidth.current) {
      firstWidth.current = false
      return
    }
    window.dispatchEvent(new Event('resize'))
  }, [sidebarWidth])


  const isAuthRoute =
    location.pathname === '/login' ||
    location.pathname === '/otp' ||
    location.pathname === '/change-password' ||
    location.pathname === '/sites'
  // The app chrome belongs to a verified session only: not while the stored token is checked, not for an anonymous visitor.
  const showChrome = !isAuthRoute && isAuthenticated && !isLoading

  return (
    <div
      style={{
        backgroundColor: pageBackground,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Box
        data-testid="app-content"
        sx={{
          marginRight: !showChrome ? 0 : `${sidebarWidth}px`,
          paddingRight: !showChrome ? 0 : 'clamp(8px, 0.75vw, 16px)',
          boxSizing: 'border-box',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: pageBackground,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/otp" element={<OtpPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Navigate to="/sites" replace />} />
            <Route path="/sites" element={<SiteSelection />} />

            <Route path="/:siteId" element={<SiteProtectedRoute />}>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<Dashboard />} />

              <Route path="consumers" element={<ConsumersLayout />}>
                <Route index element={<Consumers />} />
                <Route path=":consumerId" element={<ConsumerDetailsLayout />}>
                  <Route index element={<Navigate to="details" replace />} />
                  <Route path="details" element={<ConsumerDetails />} />
                  <Route path="readings" element={<ConsumerCalls />} />
                  <Route
                    path="notifications"
                    element={<ConsumerNotifications />}
                  />
                  <Route path="history" element={<ConsumerHistory />} />
                  <Route path="crm" element={<ConsumerCRM />} />
                  <Route path="general" element={<General />} />
                </Route>
              </Route>

              <Route
                path="maps"
                element={<Map sidebarWidth={sidebarWidth} />}
              />

              <Route path="analytics" element={<AnalyticsLayout />}>
                <Route index element={<Analytics />} />
                {/* Two zone types, two routes. :consumerId is a group id on both
                    (the Gateway stores both zone types in core.groups). */}
                <Route
                  path="tree/:consumerId"
                  element={<AnalyticsDetailsLayout kind="tree" />}
                >
                  <Route
                    path="details"
                    element={<AnalyticsDetails kind="tree" />}
                  />
                  <Route
                    path="AnalyticsConsumerTable"
                    element={<AnalyticsConsumerTable />}
                  />
                  <Route path="WaterTreeMap" element={<WaterTreeMap />} />
                </Route>
                <Route
                  path="group/:consumerId"
                  element={<AnalyticsDetailsLayout kind="group" />}
                >
                  <Route
                    path="details"
                    element={<AnalyticsDetails kind="group" />}
                  />
                  <Route
                    path="AnalyticsConsumerTable"
                    element={<AnalyticsConsumerTable />}
                  />
                  <Route path="WaterTreeMap" element={<WaterTreeMap />} />
                </Route>
              </Route>

              <Route path="crm" element={<CRM />} />
              <Route path="history" element={<History />} />
              <Route path="notifications" element={<Notifications />}>
                <Route index element={<NotificationsGeneral />} />
                <Route path="addntion-actions" element={<Details />} />
                <Route
                  path="manage-notifications"
                  element={<NotificationManage />}
                />
              </Route>
              <Route
                path="reportGeneration"
                element={<ReportGeneration />}
              />
              <Route path="sensors" element={<SensorsLayout />}>
                <Route index element={<Sensors />} />
                <Route path="graph" element={<SensorsGraph />} />
                <Route path=":sensorId" element={<SensorsDetailsLayout />}>
                  <Route index element={<Navigate to="details" replace />} />
                  <Route path="details" element={<SensorDetails />} />
                  <Route path="calls" element={<ConsumerCalls />} />
                  <Route
                    path="notifications"
                    element={<ConsumerNotifications />}
                  />
                  <Route path="history" element={<SensorsHistory />} />
                  <Route path="crm" element={<ConsumerCRM />} />
                  <Route path="general" element={<SensorsGeneral />} />
                </Route>
              </Route>
              <Route path="settings" element={<Setting />}>
                <Route
                  index
                  element={<Navigate to="users" replace />}
                />
                <Route path="users" element={<UsersSettings />} />
                <Route path="organization" element={<Organization />} />
                <Route path="categories" element={<Box>categories</Box>} />
              </Route>
              <Route path="support" element={<div>Support Page</div>} />
              <Route
                path="notifications/manage-notifications/:notificationId/flow-chart"
                element={<NotificationFlow />}
              />
              <Route path="*" element={<div>404 Not Found</div>} />
            </Route>
          </Route>
        </Routes>
        </Box>
      </Box>

      {showChrome && <Sidebar onSidebarChange={handleSidebarChange} />}
    </div>
  )
}

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App
